// Generated by tutorials/record.cjs from tutorials/articles/h22-allergies-likes-dislikes.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h22 --text-only` (wording) or
// `node tutorials/record.cjs h22` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'allergies-likes-dislikes',
  catalogId: 'h22',
  title: 'Allergies, likes and dislikes',
  summary: 'Record a resident\'s food allergies so those foods stay off the tray, and add the foods they dislike so the kitchen never serves them.',
  audience: 'Dietary managers and staff who interview residents about their food',
  area: 'residents',
  learn: [
    'Record food and drug allergies',
    'Tell a linked allergy from a reminder only',
    'See the adjustment an allergy adds',
    'Add a dislike so the item is never served',
    'Check the result on the tray ticket',
  ],
  keywords: [
    'allergy',
    'allergies',
    'food allergy',
    'drug allergy',
    'likes',
    'dislikes',
    'preferences',
    'adjustment',
    'adjustments',
    'never serve',
    'replace with',
    'substitute',
    'tray ticket',
  ],
  steps: [
    {
      id: 'food-allergies',
      part: 'Allergies',
      heading: 'Pick the food allergies',
      text: 'Open the resident and select Food Allergies on the left. Pick each allergy from the list, one per box.\n\nA red box means the allergy is linked to an adjustment that is not on the resident yet. Dietech adds it when you save.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/food-allergies.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/food-allergies.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Food Allergies section with Peanuts picked in Allergy 1, shown in red, with a note that the adjustment will be added on save.',
      },
    },
    {
      id: 'drug-allergies',
      part: 'Allergies',
      heading: 'Type any drug allergies',
      text: 'Type drug allergies in Drug Allergies. Separate them with commas.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/drug-allergies.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/drug-allergies.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.3,
        },
        alt: 'Drug Allergies with CODEINE typed in.',
      },
    },
    {
      id: 'save-allergies',
      part: 'Allergies',
      heading: 'Save',
      text: 'Select Save Changes at the top right. Dietech saves the allergies, adds the adjustment for each linked allergy, and takes you back to the Residents page.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/save-allergies.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/save-allergies.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.8,
        },
        alt: 'The Residents page after saving, with Marsh, Theodore selected and a message that the resident was saved.',
      },
    },
    {
      id: 'adjustments',
      part: 'Likes and dislikes',
      heading: 'Open Adjustments',
      text: 'Open the resident again and select Adjustments. Adjustments change what the resident gets from the menu.\n\nThe peanut allergy is here now, as a Never Serve for the PEANUT allergy group: no item in that group is served.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/adjustments.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/adjustments.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.8,
        },
        alt: 'The Adjustments section with one row: Never Serve, Allergy Group, PEANUT, at breakfast, lunch and supper.',
      },
    },
    {
      id: 'new-dislike',
      part: 'Likes and dislikes',
      heading: 'Add a dislike',
      text: 'Select the arrow next to Add Global, then New (This Resident Only). For Type, pick Item, then search for the food the resident dislikes. For Action, pick Never Serve.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/new-dislike.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/new-dislike.mp4',
          width: 1280,
          height: 742,
          durationSec: 6,
        },
        alt: 'The New Adjustment window: Type Item, Item Glazed Carrots, Action Never Serve, at breakfast, lunch and supper.',
      },
    },
    {
      id: 'days',
      part: 'Likes and dislikes',
      heading: 'Pick the meals and days',
      text: 'Clear a mealtime on the first tab if the dislike is for some meals only. For particular days, or a start and end date, select Days.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/days.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/days.mp4',
          width: 1280,
          height: 742,
          durationSec: 2.5,
        },
        alt: 'The Days tab: breakfast, lunch and supper checked for every day of the week, and an optional date range.',
      },
    },
    {
      id: 'save-dislike',
      part: 'Likes and dislikes',
      heading: 'Save the dislike',
      text: 'Select Save. The dislike is listed under its action with the meals it applies to.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/save-dislike.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/save-dislike.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.5,
        },
        alt: 'The Adjustments list with Never Serve rows for the PEANUT allergy group and the item Glazed Carrots.',
      },
    },
    {
      id: 'ticket',
      part: 'Likes and dislikes',
      heading: 'Check the tray ticket',
      text: 'Select Tray Ticket Visualizer and pick a meal. The allergy prints at the top in red, and the item the resident dislikes is no longer on their lunch.',
      media: {
        image: {
          src: '/help/allergies-likes-dislikes/ticket.webp',
          width: 1280,
          height: 794,
        },
        clip: {
          src: '/help/allergies-likes-dislikes/ticket.mp4',
          width: 1280,
          height: 794,
          durationSec: 5.4,
        },
        alt: 'The Tray Ticket Visualizer at lunch for Marsh, Theodore: ALLERGY: Peanuts at the top, and the lunch items without Glazed Carrots.',
      },
    },
  ],
  tips: [
    'Ask about likes and dislikes at admission and again at each care conference. Food that is eaten is the best supplement.',
    'To give a resident something they like at every meal, such as decaf coffee at breakfast, add it to their meal pattern. See Meal patterns.',
    'To swap a disliked item for one the resident likes, pick Replace With as the Action instead of Never Serve, then pick the item they get instead.',
  ],
  notes: [
    'An allergy in yellow is a reminder only: it prints on the tray ticket but does not take anything off the tray. Ask whoever manages your food allergy list to link it, then save the resident again.',
    'Drug allergies print as you type them and do not change what is served.',
    'Adjustments for this resident only stay with this resident. Global adjustments, added with Add Global, are shared, and a change to one reaches every resident who has it.',
  ],
  related: ['adjustments', 'admit-resident', 'meal-patterns', 'tray-tickets'],
  recorded: '2026-11-03',
};

export default article;
